import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["validate"],
  _excluded2 = ["errorMessages"],
  _excluded3 = ["dirty", "errorMessages", "inputType", "name", "onUpdate", "validate", "watchChange", "watchBlur", "watchFocus", "active", "children", "error", "ignore", "onBlur", "onChange", "onFocus", "render", "required", "touched", "value", "includeThousandsSeparator"];
/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { genUID, cherryPickKeys } from '@entrata/utils';
import { keyframes } from '@emotion/core';
import { FormActionTypes } from "../types";
import { isValueEmpty, defaultErrorMessages } from "../utils";
import { FieldPropTypes } from "../propTypes";
const AUTOFILL_KEYFRAMES = {
  start: keyframes`
    to: {}
    from: {}
  `,
  cancel: keyframes`
    to: {}
    from: {}
  `
};

/** Use this PureComponent inside the `Form` component.
 * It will inject its child with the appropriate state/handlers. */
class FieldAdapter extends PureComponent {
  constructor(props) {
    var _this;
    super(props);
    _this = this;
    this.validate = function (value) {
      if (value === void 0) {
        value = _this.props.value;
      }
      const _this$props = _this.props,
        {
          validate
        } = _this$props,
        rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
      return typeof validate === 'function' ? validate(_extends({}, rest, {
        value
      })) : '';
    };
    this.getValue = val => {
      if (val && val.target) {
        if (val.target.type === 'checkbox') {
          return val.target.checked;
        }
        return val.target.value;
      }
      return val;
    };
    this.getErrorMessage = code => {
      const _this$props2 = this.props,
        {
          errorMessages
        } = _this$props2,
        rest = _objectWithoutPropertiesLoose(_this$props2, _excluded2);
      // eslint-disable-next-line no-nested-ternary
      return errorMessages && errorMessages[code] ? typeof errorMessages[code] === 'function' ? errorMessages[code](rest) : errorMessages[code] : code;
    };
    this.getWatchProps = cherryPickKeys(['active', 'dirty', 'error', 'ignore', 'touched', 'value', 'required', 'name']);
    // Currency Precision
    this.convertDecimalSymbol = (value, from, to) => {
      return String(value).replace(/[,.$]/g, match => {
        switch (match) {
          case from:
            return to;
          default:
            return match;
        }
      });
    };
    this.blur = e => {
      var _window, _window2, _window2$__LOCALE_DAT, _window3, _window4, _window4$__LOCALE_DAT, _window5, _window6, _window6$__LOCALE_DAT;
      const {
        debug,
        onUpdate,
        watchBlur,
        allowCurrencyPrecision,
        requireDecimal
      } = this.props;
      debug && console.log('FIELD - blur');
      const isCurrency = this.props.hasOwnProperty('allowCurrencyPrecision');
      if (typeof this.props.active === 'undefined') {
        this.setState({
          active: false
        });
      }
      let {
        value
      } = this.props;
      let currencyValue;
      // convert the value typed to raw value
      if (value && String(value).indexOf(i18nUtils.getCurrencySymbol()) !== -1) {
        value = i18nUtils.getNumberUtils().getRawValue(value);
        if (i18nUtils.getNumberUtils().format.decimal === ',') {
          value = this.convertDecimalSymbol(value, '.', ',');
        }
      }
      if (i18nUtils.getNumberUtils().format.decimal === ',') {
        value = this.convertDecimalSymbol(value, ',', '.');
      }
      const isCurrencyandRequireDecimal = isCurrency && requireDecimal;
      if (isCurrencyandRequireDecimal) {
        currencyValue = this.props.value && this.props.value.length > 0 ? Number(value).toFixed(2) : '';
      }
      currencyValue = typeof window !== 'undefined' && (_window = window) !== null && _window !== void 0 && _window.__LOCALE_DATA__ && (_window2 = window) !== null && _window2 !== void 0 && (_window2$__LOCALE_DAT = _window2.__LOCALE_DATA__) !== null && _window2$__LOCALE_DAT !== void 0 && _window2$__LOCALE_DAT.boolAllowDecimalPrecision && allowCurrencyPrecision && this.props.value && !Number.isNaN(Number(value)) ? window.i18nUtils.currencyRounder().round(value) : currencyValue;
      if (i18nUtils.getNumberUtils().format.decimal === ',') {
        currencyValue = this.convertDecimalSymbol(currencyValue, '.', ',');
      }
      // eslint-disable-next-line

      onUpdate && onUpdate({
        type: FormActionTypes.onBlur,
        name: this.name,
        active: false,
        error: this.validate(),
        touched: true,
        value: typeof window !== 'undefined' && (_window3 = window) !== null && _window3 !== void 0 && _window3.__LOCALE_DATA__ && (_window4 = window) !== null && _window4 !== void 0 && (_window4$__LOCALE_DAT = _window4.__LOCALE_DATA__) !== null && _window4$__LOCALE_DAT !== void 0 && _window4$__LOCALE_DAT.boolAllowDecimalPrecision && allowCurrencyPrecision && isCurrency && this.props.value ? currencyValue : isCurrencyandRequireDecimal && this.props.value ? currencyValue : this.props.value
      });
      let currencyValueRaw = currencyValue;
      if (i18nUtils.getNumberUtils().format.decimal === ',') {
        currencyValueRaw = this.convertDecimalSymbol(currencyValue, ',', '.');
      }
      if (typeof window !== 'undefined' && (_window5 = window) !== null && _window5 !== void 0 && _window5.__LOCALE_DATA__ && (_window6 = window) !== null && _window6 !== void 0 && (_window6$__LOCALE_DAT = _window6.__LOCALE_DATA__) !== null && _window6$__LOCALE_DAT !== void 0 && _window6$__LOCALE_DAT.boolAllowDecimalPrecision && allowCurrencyPrecision && isCurrency && Number(currencyValueRaw) !== Number(value) && this.props.value) {
        this.change(currencyValue);
      }
      watchBlur && watchBlur(_extends({}, this.getWatchProps(this.props), {
        error: this.validate()
      }), e);
    };
    this.change = function (valueOrEvent, _temp) {
      var _window7, _window8, _window8$__LOCALE_DAT, _window9, _window9$__LOCALE_DAT;
      let {
        error,
        forceValidate,
        setTouched
      } = _temp === void 0 ? {} : _temp;
      const decimalSymbol = i18nUtils.getNumberUtils().format.decimal;
      const {
        debug,
        onUpdate,
        watchChange,
        allowCurrencyPrecision
      } = _this.props;
      const value = _this.getValue(valueOrEvent);
      const isCurrency = _this.props.hasOwnProperty('allowCurrencyPrecision');
      const stateUpdate = {
        value
      };
      if (error !== undefined) {
        stateUpdate.error = error;
      } else if (forceValidate || _this.props.error || isValueEmpty(value) || /^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
        stateUpdate.error = _this.validate(value);
      }
      if (setTouched) {
        stateUpdate.touched = true;
      }
      if (typeof window !== 'undefined' && (_window7 = window) !== null && _window7 !== void 0 && _window7.__LOCALE_DATA__ && (_window8 = window) !== null && _window8 !== void 0 && (_window8$__LOCALE_DAT = _window8.__LOCALE_DATA__) !== null && _window8$__LOCALE_DAT !== void 0 && _window8$__LOCALE_DAT.boolAllowDecimalPrecision && ((_window9 = window) === null || _window9 === void 0 ? void 0 : (_window9$__LOCALE_DAT = _window9.__LOCALE_DATA__) === null || _window9$__LOCALE_DAT === void 0 ? void 0 : _window9$__LOCALE_DAT.decimalPrecision) === '1' && isCurrency && allowCurrencyPrecision && String(stateUpdate.value).includes(decimalSymbol)) {
        stateUpdate.value = stateUpdate.value.replace(decimalSymbol, '');
        return null;
      }
      debug && console.log('FIELD - change', _this.name, stateUpdate);
      watchChange && watchChange(_this.getWatchProps(_extends({}, _this.props, stateUpdate)));
      onUpdate && onUpdate(_extends({
        type: FormActionTypes.onChange,
        name: _this.name
      }, stateUpdate));
    };
    this.focus = e => {
      const {
        active,
        debug,
        onUpdate,
        watchFocus
      } = this.props;
      debug && console.log('FIELD - focus');
      if (typeof active === 'undefined') {
        this.setState({
          active: true
        });
      }
      watchFocus && watchFocus(this.getWatchProps(this.props), e);
      onUpdate && onUpdate({
        type: FormActionTypes.onFocus,
        name: this.name,
        active: true,
        value: this.props.value
      });
    };
    this.handleAutoFill = e => {
      if (e.animationName === AUTOFILL_KEYFRAMES.start) {
        this.setState({
          autofill: 'true'
        });
      } else if (e.animationName === AUTOFILL_KEYFRAMES.cancel) {
        this.setState({
          autofill: 'false'
        });
      }
    };
    this.name = props.name;
    this.id = props.id || `${genUID()}-${this.name}`;
    this.state = {
      active: false,
      autofill: 'false'
    };
  }
  render() {
    const _this$props3 = this.props,
      {
        onUpdate,
        active,
        children,
        error,
        ignore,
        onBlur,
        onChange,
        onFocus,
        render,
        required,
        touched,
        value
      } = _this$props3,
      rest = _objectWithoutPropertiesLoose(_this$props3, _excluded3);
    const invalid = Boolean(error && touched);
    const {
      id
    } = this;
    const onAnimationStart = this.handleAutoFill;
    const renderProps = _extends({}, rest, {
      // eslint-disable-next-line react/destructuring-assignment
      autofill: this.state.autofill,
      // eslint-disable-next-line react/destructuring-assignment
      active: typeof active === 'undefined' ? this.state.active : active,
      error: this.getErrorMessage(error),
      hidden: ignore,
      id,
      invalid,
      name: this.name,
      onBlur: onBlur || this.blur,
      onChange: onChange || this.change,
      onFocus: onFocus || this.focus,
      onUpdate,
      onAnimationStart,
      required,
      value
    });
    return children ? children(renderProps) : render(renderProps);
  }
}
FieldAdapter.propTypes = _extends({}, FieldPropTypes.propTypes, {
  children: PropTypes.func,
  inlinelabel: PropTypes.bool,
  render: PropTypes.func
});
FieldAdapter.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  errorMessages: defaultErrorMessages
};
export { FieldAdapter, AUTOFILL_KEYFRAMES };